/** @typedef {'byok'|'platform'|'unavailable'} AiProviderAvailabilityStatus */

/**
 * @typedef {{ status: AiProviderAvailabilityStatus, available: boolean }} AiProviderSlot
 * @typedef {{
 *   companyId: string,
 *   requireTenantApiKeys: boolean,
 *   providers: {
 *     google: AiProviderSlot,
 *     anthropic: AiProviderSlot,
 *     local: AiProviderSlot,
 *     deepseek: AiProviderSlot,
 *   },
 * }} AiProviderAvailability
 */

/** Tooltip when status === 'platform' (shared platform key, not company BYOK). */
export const PLATFORM_KEY_TOOLTIP =
  'Uses the shared platform API key (not your company BYOK key).';

export const PLATFORM_LOCAL_TOOLTIP =
  'Uses the shared platform local model endpoint (not your company BYOK).';

export const UNAVAILABLE_PROVIDER_TOOLTIP =
  'Provider unavailable — add a company API key under Workspace Settings → API & integrations, or ask an admin to enable platform fallback.';

/**
 * Map modelTarget id → provider key used in availability payload.
 * @param {string} modelId
 * @returns {'google'|'anthropic'|'local'|'deepseek'}
 */
export function providerKeyFromModelId(modelId) {
  const raw = typeof modelId === 'string' ? modelId.trim().toLowerCase() : '';
  if (raw.startsWith('local:') || raw === 'local') return 'local';
  if (raw.startsWith('claude-') || raw === 'claude') return 'anthropic';
  if (raw.startsWith('deepseek-') || raw === 'deepseek') return 'deepseek';
  return 'google';
}

/**
 * @param {AiProviderAvailability | null | undefined} availability
 * @param {string} modelId
 * @returns {boolean}
 */
export function isModelTargetAvailable(availability, modelId) {
  if (!availability?.providers) return false;
  const key = providerKeyFromModelId(modelId);
  return availability.providers[key]?.available === true;
}

/**
 * @param {AiProviderAvailability | null | undefined} availability
 * @param {string} modelId
 * @returns {string}
 */
export function modelTargetAvailabilityTooltip(availability, modelId) {
  if (!availability?.providers) return UNAVAILABLE_PROVIDER_TOOLTIP;
  const key = providerKeyFromModelId(modelId);
  const status = availability.providers[key]?.status;
  if (status === 'platform') {
    return key === 'local' ? PLATFORM_LOCAL_TOOLTIP : PLATFORM_KEY_TOOLTIP;
  }
  if (status === 'byok') return '';
  return UNAVAILABLE_PROVIDER_TOOLTIP;
}

/**
 * Submit guard: allow leaving an edit baseline even when that model is now
 * unavailable; block hire / changes that select an unavailable target.
 *
 * @param {{
 *   isEdit: boolean,
 *   baselineModelTarget: string | null,
 *   modelTarget: string,
 *   availability: AiProviderAvailability | null | undefined,
 *   normalizeModelTarget: (id: string) => string,
 * }} opts
 * @returns {{ ok: true, resolvedModel: string } | { ok: false, error: string }}
 */
export function evaluateModelTargetSubmit({
  isEdit,
  baselineModelTarget,
  modelTarget,
  availability,
  normalizeModelTarget,
}) {
  const resolvedModel = normalizeModelTarget(modelTarget);
  const leavingExistingModel =
    isEdit &&
    baselineModelTarget != null &&
    resolvedModel === baselineModelTarget;
  const modelAvailable = isModelTargetAvailable(availability, resolvedModel);

  if (!modelAvailable && !leavingExistingModel) {
    return {
      ok: false,
      error:
        modelTargetAvailabilityTooltip(availability, resolvedModel) ||
        UNAVAILABLE_PROVIDER_TOOLTIP,
    };
  }

  return { ok: true, resolvedModel };
}
